/**
 * MetaColumnHeaderCell.tsx
 * Collapsible header cell for the Job Class and Work Group meta-columns in the
 * Skill Matrix grid.
 *
 * Renders either:
 *   - Expanded: normal label text with an ExpandMoreIcon chevron, click to
 *     collapse.
 *   - Collapsed: narrow cell (COLLAPSED_GROUP_WIDTH) with the label rotated
 *     vertical and a ChevronRightIcon, click to expand.
 *
 * Matches the visual pattern used for category group headers in
 * SkillMatrixGrid.tsx — same icon sizes, font tokens, hover bg, and color.
 */

import React from 'react';
import { Box, TableCell } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';

/** Must match the constant in SkillMatrixGrid.tsx. */
const COLLAPSED_GROUP_WIDTH = 50;

interface MetaColumnHeaderCellProps {
  /** Display label shown in the header (e.g. "Job Class", "Work Group"). */
  label: string;
  /** Whether this column is currently collapsed. */
  collapsed: boolean;
  /** Shared sticky-header sx to apply to both states. */
  stickyHeaderSx: Record<string, unknown>;
  /** Called when the header is clicked to toggle collapse. */
  onToggle: () => void;
}

/**
 * Renders a single collapsible TableCell for a meta-column header.
 * Use as a direct child of a <TableRow> in the column-header <TableHead> row.
 */
export const MetaColumnHeaderCell: React.FC<MetaColumnHeaderCellProps> = ({
  label,
  collapsed,
  stickyHeaderSx,
  onToggle,
}) => {
  const theme = useTheme();

  if (collapsed) {
    return (
      <TableCell
        align="center"
        onClick={onToggle}
        sx={{
          ...stickyHeaderSx,
          top: 33,
          width: COLLAPSED_GROUP_WIDTH,
          minWidth: COLLAPSED_GROUP_WIDTH,
          maxWidth: COLLAPSED_GROUP_WIDTH,
          color: theme.palette.primary.main,
          cursor: 'pointer',
          userSelect: 'none',
          p: 0.5,
          '&:hover': { bgcolor: theme.table.hoverBg },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
          }}
        >
          <ChevronRightIcon
            sx={{ fontSize: '0.85rem', color: theme.palette.primary.main }}
          />
          <Box
            component="span"
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              transform: 'rotate(180deg)',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              lineHeight: 1.2,
              maxHeight: 90,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </Box>
        </Box>
      </TableCell>
    );
  }

  return (
    <TableCell
      onClick={onToggle}
      sx={{
        ...stickyHeaderSx,
        top: 33,
        color: theme.palette.primary.main,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: theme.table.hoverBg },
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <ExpandMoreIcon
          sx={{ fontSize: '0.85rem', color: theme.palette.primary.main }}
        />
        {label}
      </Box>
    </TableCell>
  );
};
